import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnDestroy,
    AfterViewInit,
    OnChanges,
    SimpleChanges,
    ViewChild,
    ElementRef,
} from '@angular/core';

const Editor = require('@toast-ui/editor');
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as _ from 'lodash';
import { MentionListComponent } from '../gf-mentions/mention-list.component';

import { MentionItem, MentionListConfig } from '../../core/services/core.mentio.service';
import { GfTranslateService } from '../../core/services/core.translate.service';
import { looksLikeHtml } from '../../core/services/markdown-content.functions';
import { escapeHtml } from '../../core/services/core.utils.service';
import { MarkdownEditorService } from '../../core/services/core.markdown-editor.service';
import { UiService } from '../../core/services/core.ui.service';
import { UploadService } from '../../core/services/core.upload.service';
import { UserRest } from '../../core/services/core.user.rest.service';
import { UserService } from '../../core/services/core.user.service';
import { WikiVideoInsertModalComponent } from '../wiki/video-insert-modal/wiki.video-insert-modal.component';

/**
 * Lightweight inline Markdown editor wrapping ToastUI.
 *
 * UX intent (different from `wiki-markdown-editor`):
 *  - Always inline, auto-height.
 *  - Formatting toolbar is hidden by default and revealed on focus, hidden on blur.
 *  - A Raw / WYSIWYG toggle group (active mode highlighted)
 *  - No preview, side-by-side, save, cancel, or help affordances — the host owns
 *    save/cancel and the editor only reports content via `(contentChange)`.
 *
 * Output is always Markdown regardless of which editing mode the user picked.
 */
@Component({
    selector: 'gf-markdown-editor',
    template: require('./gf-markdown-editor.component.html'),
})
export class GfMarkdownEditorComponent implements OnDestroy, AfterViewInit, OnChanges {
    @ViewChild('editorContainer', { static: false }) editorContainer!: ElementRef;

    /** The @mention autocomplete popup, and its host element for positioning. */
    @ViewChild('mentionList', { static: false }) mentionListCmp!: MentionListComponent;
    @ViewChild('mentionList', { static: false, read: ElementRef }) mentionListEl!: ElementRef;

    /** The Raw/WYSIWYG toggle group — measured to reserve toolbar width, see bindModeToggleReserve. */
    @ViewChild('modeToggle', { static: false, read: ElementRef }) modeToggleEl!: ElementRef;

    /** Initial markdown content. Updates from the host are applied via setMarkdown. */
    @Input() content: string = '';

    /** Reference id for image upload (e.g. tracker item id, message id). */
    @Input() refId: number = 0;

    /** UploadService section (e.g. 'trackeritem', 'trackeritem_message'). */
    @Input() uploadSection: string = '';

    /** Preference section key used to persist Raw/WYSIWYG mode. */
    @Input() preferenceSection: string = '';

    /** Mode the editor opens in. */
    @Input() initialEditType: 'markdown' | 'wysiwyg' = 'wysiwyg';

    /** Hide the editor entirely when true. */
    @Input() readOnly: boolean = false;

    /** Placeholder shown when the editor is empty. */
    @Input() placeholder: string = '';

    /** Minimum height for the editor body. */
    @Input() minHeight: string = '120px';

    /**
     * Editor height. 'auto' grows with the content, which is what a compose box
     * wants; a full-page editor passes a fixed height and lets its body scroll.
     */
    @Input() height: string = 'auto';

    /**
     * Markdown preview layout. 'vertical' is the side-by-side pane, which only a
     * full-page editor has room for.
     */
    @Input() previewStyle: 'tab' | 'vertical' = 'tab';

    /**
     * MIME type of `content`, when the host knows it authoritatively.
     *
     * Left unset, the editor sniffs with looksLikeHtml() — right for tracker and
     * forum, where legacy TinyMCE HTML and new Markdown share a column with
     * nothing to tell them apart. A host that stores the format alongside the
     * content should say so rather than let a heuristic decide.
     */
    @Input() contentFormat?: 'text/html' | 'text/markdown';

    /**
     * Render the built-in Raw / WYSIWYG toggle.
     *
     * Off for a host that puts the mode buttons in its own chrome; it then drives
     * the editor through `setMode()` and reads `currentMode`.
     */
    @Input() showModeSwitch: boolean = true;

    /** Mention list configs, as built by MentionsService.createMentionConfig(). */
    @Input() mentions: MentionListConfig[] = [];

    /**
     * Treat a bare Enter as "submit" rather than a newline, leaving
     * Shift+Enter to insert the line break. For compose-box hosts where a
     * message is usually one line and sending is the common case.
     *
     * Off by default: in a tracker follow-up or release notes, Enter must keep
     * behaving like Enter.
     */
    @Input() submitOnEnter: boolean = false;

    /**
     * Put the caret in the editor as soon as it mounts.
     *
     * Off by default, unlike ToastUI's own `autofocus` option. A widget that
     * grabs focus on mount scrolls the page to itself and takes the keyboard
     * from whatever the user was doing -- and in compact mode it also pops the
     * toolbar open, since that is revealed by :focus-within and cannot tell an
     * automatic focus from a deliberate one.
     *
     * Hosts that want focus on open should opt in here rather than relying on
     * the library default.
     */
    @Input() autofocus: boolean = false;

    /**
     * Single-line compose box: rests one line tall and grows upward as content
     * is added, with the toolbar floating above the box rather than occupying
     * a row inside it -- so it is available even on a one-line message without
     * the box changing height.
     *
     * The editor is already `height: 'auto'`, so growth is free; what this adds
     * is the single-line resting state, the ceiling, and the floating toolbar.
     */
    @Input() compact: boolean = false;

    /**
     * Which shared toolbar to render — see MarkdownEditorService.buildToolbarItems.
     *
     * The product default is the full set: anywhere a user writes content that
     * ends up rendered as a document — tracker details and follow-ups, forum
     * posts, project descriptions, notification templates — gets headings,
     * hr/quote and list indentation.
     *
     * Hosts pass `'essential'` when the editor sits somewhere too constrained
     * for that: the embedded chat compose box, a modal dialog, or a tracker
     * custom text-area field.
     */
    @Input() toolbarVariant: 'full' | 'essential' = 'full';

    /**
     * Toolbar buttons beyond the shared set, for hosts that back them with their
     * own ToastUI commands: the attachment picker, scroll-sync, and an in-toolbar
     * save. Defaults to none — a button whose command is unregistered does
     * nothing, so these stay opt-in.
     */
    @Input() toolbarOptions: { attachment?: boolean; scrollSync?: boolean; save?: boolean } = {};

    /**
     * Open the @mention popup upward, above the caret, instead of below it.
     *
     * For a compose box pinned to the bottom of the viewport -- chat, standalone
     * or embedded -- a downward list opens into space that is off screen, so the
     * matches cannot be seen or clicked. Tracker items compose mid-page and want
     * the default downward behaviour.
     */
    @Input() mentionsAbove: boolean = false;

    /**
     * Position the @mention popup against the viewport rather than the editor.
     *
     * For a host laid out inside `overflow: hidden` ancestors, which clip an
     * absolutely-positioned popup at the editor's edge as soon as the caret
     * nears the bottom of it — the full-page wiki editor's flex column does
     * exactly that. Fixed coordinates escape the clip; `overflow` clipping is
     * not a stacking-order problem, so z-index alone cannot solve it.
     */
    @Input() mentionsFixed: boolean = false;

    /**
     * Keep ToastUI's link / image / table dialogs inside the viewport.
     *
     * ToastUI positions a popup directly below the toolbar button that opened
     * it and clamps it *horizontally only* (see Popup.updated() in the
     * library — it compares against the toolbar width and nothing else). For
     * an editor sitting at the bottom of the screen, as a compose box
     * does, every dialog therefore opens downward into space that is not on
     * screen, and cannot be reached or dismissed.
     *
     * With this on, a popup that would fall past the bottom edge is flipped to
     * open upward from its button instead, and nudged back in horizontally if
     * needed.
     */
    @Input() keepPopupsInView: boolean = false;

    /** Emits the current Markdown on every edit, debounced. */
    @Output() contentChange = new EventEmitter<string>();

    /** Emits mention search term, mirroring old editor behavior. */
    @Output() searchTerm = new EventEmitter<string>();

    /** Emits the underlying ToastUI Editor instance once initialised. */
    @Output() editorReady = new EventEmitter<any>();

    /**
     * Fires when `submitOnEnter` is set and the user pressed Enter to send.
     *
     * The keystroke is already swallowed by the time this emits, so the host
     * only has to act on it.
     */
    @Output() submitShortcut = new EventEmitter<KeyboardEvent>();

    private editor: any = null;
    private isUpdating: boolean = false;
    private destroyed: boolean = false;
    private initTimer: any = null;
    private contentUpdateFrame: number | null = null;
    private originalWindowOnError: OnErrorEventHandler = null;
    private debouncedContentChange: _.DebouncedFunc<() => void>;
    private lastEmittedMarkdown: string = '';
    private lastMentionSearchTerm: string = '';
    private pasteListener: ((event: ClipboardEvent) => void) | null = null;
    private toolbarMouseDownListener: ((event: MouseEvent) => void) | null = null;
    private editorKeyListener: ((event: KeyboardEvent) => void) | null = null;
    private popupObserver: MutationObserver | null = null;
    private modeToggleObserver: ResizeObserver | null = null;

    /** Breathing room left between a repositioned popup and the viewport edge. */
    private static readonly POPUP_VIEWPORT_MARGIN = 8;

    /**
     * Gap left between the last toolbar item and the Raw/WYSIWYG toggle, on top
     * of the toggle's own measured width and its `right` offset (8px expanded,
     * 12px compact -- the larger is used so one number covers both layouts).
     */
    private static readonly MODE_TOGGLE_GUTTER = 16;

    /** Custom property the toolbar stylesheet reads to keep clear of the toggle. */
    private static readonly MODE_TOGGLE_RESERVE_PROP = '--gf-md-mode-toggle-reserve';

    /** The `@…` text between the trigger and the caret, or null when inactive. */
    private mentionQuery: string | null = null;

    /**
     * Whether the mention popup is showing. Public so a host can gate its own
     * Enter handling -- Enter belongs to the popup while it is open.
     */
    public mentionOpen: boolean = false;

    /** Tracks current editor mode so the toolbar button can render the right label. */
    public currentMode: 'markdown' | 'wysiwyg' = 'wysiwyg';

    /** Host classes driving the compact/expanded compose states. */
    static readonly COMPACT_CLASS = 'gf-md-editor--compact';

    /** Hides the compose toolbar while the editor still holds focus. */
    static readonly TOOLBAR_SUPPRESSED_CLASS = 'gf-md-editor--toolbar-suppressed';

    constructor(
        private gfTranslateService: GfTranslateService,
        private uiService: UiService,
        private uploadService: UploadService,
        private userRest: UserRest,
        private userService: UserService,
        private markdownEditorService: MarkdownEditorService,
        private elementRef: ElementRef,
        private modalService: NgbModal,
    ) {
        this.debouncedContentChange = _.debounce(() => {
            if (this.editor && !this.isUpdating) {
                this.lastEmittedMarkdown = this.editor.getMarkdown();
                this.contentChange.emit(this.lastEmittedMarkdown);
            }
        }, 300);
    }

    ngAfterViewInit(): void {
        this.initTimer = setTimeout(() => {
            this.initTimer = null;
            if (!this.destroyed) {
                this.initializeEditor();
            }
        }, 0);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.content && !changes.content.firstChange && this.editor) {
            // Skip echoes: when a host binds [content]="x" + (contentChange)="x = $event",
            // Angular re-runs ngOnChanges with the same value we just emitted. Calling
            // setMarkdown again would reset the cursor for no reason.
            //
            // Compare against the editor's current value in the same format the
            // host is passing. If `incoming` is HTML (legacy content), comparing
            // against `getMarkdown()` would never match because we mounted via
            // setHTML() and the editor has since converted it.
            const incoming = this.content || '';
            if (incoming === this.lastEmittedMarkdown) {
                return;
            }
            // When the user is actively typing in this editor, host echoes can
            // arrive between internal ProseMirror transactions. Re-applying
            // content mid-transaction can trigger TransformError failures.
            if (this.isEditorFocused()) {
                return;
            }
            const incomingIsHtml = this.contentLooksLikeHtml(incoming);
            const current = incomingIsHtml ? this.editor.getHTML() : this.editor.getMarkdown();
            if (incoming === current) {
                return;
            }
            if (this.contentUpdateFrame) {
                cancelAnimationFrame(this.contentUpdateFrame);
            }
            this.contentUpdateFrame = requestAnimationFrame(() => {
                this.contentUpdateFrame = null;
                if (!this.editor || this.destroyed) return;
                this.isUpdating = true;
                try {
                    if (this.contentLooksLikeHtml(incoming)) {
                        this.editor.setHTML(incoming);
                    } else {
                        this.editor.setMarkdown(incoming);
                    }
                } catch (e) {
                    console.warn('gf-markdown-editor: setContent failed', e);
                }
                this.isUpdating = false;
                this.debouncedContentChange.cancel();
            });
        }

        if (changes.readOnly && !changes.readOnly.firstChange && this.editor) {
            if (this.readOnly) {
                this.editor.hide();
            } else {
                this.editor.show();
            }
        }

        if (changes.compact) {
            this.applyCompactClass();
        }

        if (changes.mentions) {
            if (!this.isMentionsEnabled()) {
                this.resetMentionSearchState();
                this.exitMentionContext();
            } else if (this.mentionQuery !== null) {
                // Results of the async lookup the host kicked off from
                // (searchTerm) -- repaint the popup against the same query.
                this.refreshMentionDropdown();
            }
        }
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        if (this.initTimer) {
            clearTimeout(this.initTimer);
            this.initTimer = null;
        }
        if (this.contentUpdateFrame) {
            cancelAnimationFrame(this.contentUpdateFrame);
            this.contentUpdateFrame = null;
        }
        this.unbindPasteListener();
        this.unbindToolbarFocusGuard();
        this.unbindEditorKeyListener();
        this.unbindPopupViewportGuard();
        this.unbindModeToggleReserve();
        this.debouncedContentChange.cancel();
        this.destroyEditor();
        // Defer error-handler cleanup briefly so any queued ToastUI vDOM frames
        // that fire post-destroy can still be suppressed.
        setTimeout(() => {
            window.removeEventListener('error', this.handleEditorVDOMError, true);
            if (this.originalWindowOnError !== null) {
                window.onerror = this.originalWindowOnError;
                this.originalWindowOnError = null;
            }
        }, 500);
    }

    private initializeEditor(): void {
        if (!this.editorContainer?.nativeElement) {
            console.error('gf-markdown-editor: editor container missing');
            return;
        }

        this.loadPreferredMode()
            .then((preferredMode) => {
                if (this.destroyed) return;
                this.createEditor(preferredMode);
            })
            .catch(() => {
                if (this.destroyed) return;
                this.createEditor(this.initialEditType);
            });
    }

    private createEditor(initialMode: 'markdown' | 'wysiwyg'): void {
        try {
            const EditorClass = Editor.default || Editor;
            this.currentMode = initialMode;

            // Heuristic: legacy tracker content is TinyMCE-emitted HTML; new content
            // is markdown. If we pass HTML as `initialValue` (which expects markdown),
            // ToastUI displays it as literal source text — exactly "looks like plain
            // text" — even in WYSIWYG mode. So when the source looks like HTML,
            // initialise the editor empty and call setHTML() once it's ready, mirroring
            // the wiki's approach.
            const contentIsHtml = this.contentLooksLikeHtml(this.content || '');

            this.editor = new EditorClass({
                el: this.editorContainer.nativeElement,
                height: this.height,
                minHeight: this.minHeight,
                initialEditType: initialMode,
                previewStyle: this.previewStyle,
                // ToastUI defaults this to true, which makes a freshly mounted
                // editor steal focus from the page. Two reasons that is wrong
                // here: it scrolls and hijacks the keyboard on any screen that
                // merely contains an editor, and in compact mode the toolbar is
                // revealed by :focus-within -- so an automatic focus pops the
                // toolbar open before the user has touched anything.
                //
                // Hosts that genuinely want the caret waiting for the user can
                // set [autofocus]="true", or call the public focus() method.
                autofocus: this.autofocus,
                initialValue: contentIsHtml ? '' : (this.content || ''),
                usageStatistics: false,
                // ToastUI's own switch stays off either way: we render the toggle
                // in our template, or the host renders it in its chrome.
                hideModeSwitch: true,
                toolbarItems: this.getToolbarItems(),
                placeholder: this.placeholder || this.gfTranslateService.translate('Write a comment…'),
                plugins: this.markdownEditorService.buildPlugins(),
                customHTMLRenderer: {
                    image: (node: any) => {
                        const src = node.destination || '';
                        const alt = node.firstChild ? node.firstChild.literal || '' : '';
                        const embed = this.buildVideoEmbedHtml(src, alt);
                        if (embed) {
                            return [
                                { type: 'openTag', tagName: 'div', outerNewLine: true, attributes: { class: 'wiki-video-container' } },
                                { type: 'html', content: embed },
                                { type: 'closeTag', tagName: 'div', outerNewLine: true },
                            ];
                        }
                        return {
                            type: 'openTag',
                            tagName: 'img',
                            selfClose: true,
                            attributes: { src, alt },
                        };
                    },
                    codeBlock: this.markdownEditorService.buildCodeBlockRenderer(),
                    // NOT registering htmlBlock for <details>/<summary>: this makes
                    // ToastUI's WYSIWYG schema require 'block+' content for that node,
                    // and something in ToastUI's own toDOM/sanitizeDOM path crashes
                    // ("can't access property 'type', token is null") when rendering
                    // it back to DOM — confirmed via stack trace to happen during
                    // ProseMirror view rendering (updateStateInner → updateChildren →
                    // toDOM → sanitizeDOM), not during markdown parsing, so a
                    // pre-parse markdown fix-up can't prevent it. <details> stays
                    // flattened to plain text in WYSIWYG; Raw mode and the static
                    // preview pane render it correctly already.
                },
                hooks: {
                    addImageBlobHook: this.handleImageUpload.bind(this),
                },
            });

            this.installVDOMErrorSuppression();

            // Defer setHTML to next frame so it doesn't clash with the constructor's
            // initial render cycle.
            if (contentIsHtml && this.content) {
                requestAnimationFrame(() => {
                    if (this.editor && !this.destroyed) {
                        try {
                            this.editor.setHTML(this.content);
                        } catch (e) {
                            console.warn('gf-markdown-editor: setHTML failed', e);
                        }
                    }
                });
            }

            this.editor.on('change', () => {
                this.debouncedContentChange();
                this.onMentionEditorContentChanged();
                this.scheduleEnhancePreview();
            });

            this.registerVideoCommand();
            this.applyCompactClass();

            this.bindPasteListener();
            this.bindToolbarFocusGuard();
            this.bindEditorKeyListener();
            this.bindPopupViewportGuard();
            this.bindModeToggleReserve();
            this.scheduleEnhancePreview();

            this.editorReady.emit(this.editor);
        } catch (error) {
            console.error('gf-markdown-editor: init failed', error);
            this.uiService.message.showError(
                this.gfTranslateService.translate('Failed to initialize the markdown editor'),
            );
        }
    }

    /**
     * Back the toolbar's video button with a command in both editing modes.
     *
     * ToastUI resolves a button's `command` per mode, so a command registered
     * for markdown only silently does nothing in WYSIWYG (and vice versa).
     * Harmless when the button is absent (the essential variant): the command
     * simply never fires.
     */
    private registerVideoCommand(): void {
        if (!this.editor) return;
        ['markdown', 'wysiwyg'].forEach((mode) => {
            this.editor.addCommand(mode, 'insertVideo', () => {
                this.promptInsertVideo();
                return true;
            });
        });
    }

    /**
     * Ask for a video URL and insert it as an image link, which the
     * `customHTMLRenderer.image` hook above turns into a real embed. Same modal
     * and same markdown shape as the wiki editor, so a video pasted into a
     * tracker comment and one written on a wiki page render identically.
     */
    private promptInsertVideo(): void {
        const modalRef = this.modalService.open(WikiVideoInsertModalComponent, {
            backdrop: 'static',
            keyboard: true,
        });

        modalRef.componentInstance.insert.subscribe((url: string) => {
            if (this.editor) {
                this.editor.insertText(`![video](${url})`);
            }
        });
    }

    private getModePreferenceName(): string {
        return this.markdownEditorService.getModePreferenceName(this.preferenceSection, this.uploadSection, 'default');
    }

    private getSessionUnixName(): string {
        return _.get(this.userService, 'session.getUnixName', () => '')();
    }

    private loadPreferredMode(): Promise<'markdown' | 'wysiwyg'> {
        const unixName = this.getSessionUnixName();
        if (!unixName || unixName === 'None') {
            return Promise.resolve(this.initialEditType);
        }
        return this.userRest
            .getUserPreference(unixName, this.getModePreferenceName(), { default: this.initialEditType })
            .then((result: any) => this.markdownEditorService.normalizeMode(
                _.get(result, 'preferenceValue', this.initialEditType),
                this.initialEditType,
                'markdown',
                'wysiwyg',
            ))
            .catch(() => this.initialEditType);
    }

    private savePreferredMode(mode: 'markdown' | 'wysiwyg'): void {
        const unixName = this.getSessionUnixName();
        if (!unixName || unixName === 'None') return;
        const preferenceValue = mode === 'markdown' ? 'raw' : 'wysiwyg';
        this.userRest
            .saveUserPreference(unixName, this.getModePreferenceName(), preferenceValue)
            .catch(() => undefined);
    }

    private bindPasteListener(): void {
        const container = this.editorContainer?.nativeElement as HTMLElement;
        if (!container || this.pasteListener) return;
        this.pasteListener = (event: ClipboardEvent) => this.handlePasteSwitchToMarkdown(event);
        container.addEventListener('paste', this.pasteListener, true);
    }

    private unbindPasteListener(): void {
        const container = this.editorContainer?.nativeElement as HTMLElement;
        if (!container || !this.pasteListener) return;
        container.removeEventListener('paste', this.pasteListener, true);
        this.pasteListener = null;
    }

    /**
     * Keep the editor focused while the user operates the toolbar or a toolbar
     * popup. ToastUI's table size-picker is a grid of non-focusable <div> cells;
     * a plain mousedown on one would blur the contenteditable, dropping the
     * host's :focus-within state and collapsing the hide-on-blur toolbar before
     * the table is inserted. Suppressing the mousedown default keeps focus on
     * ProseMirror so the toolbar stays open and the click still registers.
     *
     * Form fields (the link/image popup inputs) are excluded so they can focus,
     * and the content area is excluded so caret placement still works.
     */
    private bindToolbarFocusGuard(): void {
        const container = this.editorContainer?.nativeElement as HTMLElement;
        if (!container || this.toolbarMouseDownListener) return;
        this.toolbarMouseDownListener = (event: MouseEvent) => {
            // Clicking anywhere in the editor means composing has resumed.
            this.releaseToolbar();

            const target = event.target as HTMLElement | null;
            if (!target) return;
            const inChrome = target.closest('.toastui-editor-defaultUI-toolbar, .toastui-editor-popup');
            const isFormField = target.closest('input, textarea, select, [contenteditable="true"]');
            if (inChrome && !isFormField) {
                event.preventDefault();
            }
        };
        container.addEventListener('mousedown', this.toolbarMouseDownListener, true);
    }

    private unbindToolbarFocusGuard(): void {
        const container = this.editorContainer?.nativeElement as HTMLElement;
        if (!container || !this.toolbarMouseDownListener) return;
        container.removeEventListener('mousedown', this.toolbarMouseDownListener, true);
        this.toolbarMouseDownListener = null;
    }

    /**
     * Keep ToastUI's toolbar clear of the Raw/WYSIWYG toggle.
     */
    private bindModeToggleReserve(): void {
        const toggle = this.modeToggleEl?.nativeElement as HTMLElement | undefined;
        if (!toggle || this.modeToggleObserver) return;
        // One-shot measure first, so the reserve is still applied on a browser
        // with no ResizeObserver -- it just will not track later changes.
        this.applyModeToggleReserve();
        if (typeof ResizeObserver === 'undefined') return;
        this.modeToggleObserver = new ResizeObserver(() => this.applyModeToggleReserve());
        this.modeToggleObserver.observe(toggle);
    }

    private unbindModeToggleReserve(): void {
        if (!this.modeToggleObserver) return;
        this.modeToggleObserver.disconnect();
        this.modeToggleObserver = null;
    }

    private applyModeToggleReserve(): void {
        const toggle = this.modeToggleEl?.nativeElement as HTMLElement | undefined;
        const host = this.elementRef?.nativeElement as HTMLElement | undefined;
        if (!toggle || !host || this.destroyed) return;
        // offsetWidth, not getBoundingClientRect: the toggle is opacity-faded
        // rather than removed when blurred, so its layout box is always real,
        // and an integer keeps the property from churning on sub-pixel zoom.
        const width = toggle.offsetWidth;
        const reserve = width ? width + GfMarkdownEditorComponent.MODE_TOGGLE_GUTTER : 0;
        const value = reserve + 'px';
        if (host.style.getPropertyValue(GfMarkdownEditorComponent.MODE_TOGGLE_RESERVE_PROP) === value) return;
        host.style.setProperty(GfMarkdownEditorComponent.MODE_TOGGLE_RESERVE_PROP, value);
    }

    private handlePasteSwitchToMarkdown(event: ClipboardEvent): void {
        this.markdownEditorService.handleMarkdownPasteSwitch({
            event,
            editor: this.editor,
            readOnly: this.readOnly,
            currentMode: this.currentMode,
            markdownMode: 'markdown',
            isUpdating: this.isUpdating,
            isDestroyed: () => this.destroyed,
            isEditorInMarkdownMode: () => this.isEditorInMarkdownMode(),
            setUpdating: (value: boolean) => {
                this.isUpdating = value;
            },
            switchToMarkdownMode: () => {
                this.editor.changeMode('markdown');
                this.currentMode = 'markdown';
                this.savePreferredMode('markdown');
            },
            onAfterInsert: () => {
                this.debouncedContentChange();
                this.onMentionEditorContentChanged();
            },
            onModeSwitchError: (error: any) => {
                console.warn('gf-markdown-editor: markdown paste mode switch failed', error);
            },
            onInsertError: (error: any) => {
                console.warn('gf-markdown-editor: markdown paste insert failed', error);
            },
        });
    }

    private isEditorInMarkdownMode(): boolean {
        if (!this.editor) return false;
        if (typeof this.editor.isMarkdownMode === 'function') {
            return !!this.editor.isMarkdownMode();
        }
        return this.currentMode === 'markdown';
    }

    /**
     * Toolbar comes from the shared factory so this editor and the wiki one
     * stay in step. Hidden by default via CSS, revealed when the editor has
     * focus. The Raw/WYSIWYG toggle is rendered as an Angular-managed sibling
     * button outside ToastUI's toolbar — safer than threading it through
     * ToastUI's command system, which conflicts with `changeMode()` calls from
     * within command handlers.
     */
    private getToolbarItems(): any[][] {
        return this.markdownEditorService.buildToolbarItems({
            variant: this.toolbarVariant,
            // Only reaches the bar on the full variant — the factory drops it
            // for 'essential', so chat and other constrained hosts stay clean.
            video: true,
            // Attachment / scroll-sync / save, for a host that registers the
            // commands behind them. Empty by default.
            ...this.toolbarOptions,
            translate: (key: string) => this.gfTranslateService.translate(key),
        });
    }

    /**
     * Switch to an explicit mode from the Raw / WYSIWYG toggle group.
     * No-op when already in that mode so re-clicking the active button does
     * nothing (and avoids a redundant preference save + br-tag cleanup pass).
     */
    public setMode(mode: 'markdown' | 'wysiwyg'): void {
        if (!this.editor || this.currentMode === mode) return;
        this.editor.changeMode(mode);
        this.currentMode = mode;
        this.savePreferredMode(mode);
        if (mode === 'markdown') {
            this.cleanupBrTagsAfterModeSwitch();
        }
        this.scheduleEnhancePreview();
    }

    /**
     * Re-run Prism/Mermaid/KaTeX enhancement on whichever ToastUI content pane(s)
     * are currently rendered. Deferred two animation frames so ToastUI's own
     * (rAF-throttled) preview/WYSIWYG re-render has finished first.
     */
    private scheduleEnhancePreview(): void {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (this.destroyed || !this.editorContainer?.nativeElement) return;
                this.markdownEditorService.enhanceContainer(this.editorContainer.nativeElement);
            });
        });
    }

    /**
     * ToastUI's WYSIWYG→Markdown serialiser converts empty paragraphs to standalone
     * `<br>` tags.  After any manual switch to markdown mode, strip them so the user
     * sees clean source.  Skips when `isUpdating` is true because an in-flight paste
     * insertion has already provided sanitised content via cleanBrTags().
     */
    private cleanupBrTagsAfterModeSwitch(): void {
        this.markdownEditorService.cleanupBrTagsAfterModeSwitch({
            editor: this.editor,
            isDestroyed: () => this.destroyed,
            isUpdating: () => this.isUpdating,
            setUpdating: (value: boolean) => {
                this.isUpdating = value;
            },
        });
    }

    /**
     * Is `content` HTML (TinyMCE-emitted, structural tags) rather than Markdown?
     *
     * An explicit `contentFormat` from the host settles it: a host that stores
     * the MIME alongside the content knows better than any heuristic.
     *
     * Without one, fall back to the heuristic — right for tracker and forum,
     * where legacy HTML and new Markdown share a column with nothing to tell
     * them apart. Mirrors the backend MarkdownService::looksLikeHtml, and is
     * shared with `gf-markdown-viewer`, so read- and write-paths agree.
     */
    private contentLooksLikeHtml(content: string): boolean {
        if (this.contentFormat) {
            return this.contentFormat === 'text/html';
        }
        return looksLikeHtml(content);
    }

    private handleImageUpload(blob: File | Blob, callback: (url: string, altText: string) => void): void {
        const fileName = (blob as File).name || 'image.png';
        this.uploadService
            .upload(this.uploadSection, this.refId, blob, fileName)
            .then((location: unknown) => {
                callback(String(location), fileName);
                this.uiService.message.showSuccess(this.gfTranslateService.translate('Image uploaded successfully'));
            })
            .catch((error) => {
                console.error('gf-markdown-editor: image upload failed', error);
                this.uiService.message.showError(this.gfTranslateService.translate('Failed to upload image'));
            });
    }

    /**
     * Render images that point at video URLs (mp4/webm/ogg/mov, YouTube, Vimeo)
     * as actual video players inside the WYSIWYG view and the Markdown preview.
     */
    private buildVideoEmbedHtml(url: string, alt: string = ''): string | null {
        const escapedAlt = escapeHtml(alt);
        const caption = alt ? '<p class="wiki-video-caption">' + escapedAlt + '</p>' : '';

        if (/\.(mp4|webm|ogg|mov)(\?[^"']*)?$/i.test(url)) {
            const escaped = escapeHtml(url);
            return (
                '<video controls preload="metadata" class="wiki-video-player"><source src="' +
                escaped +
                '">Your browser does not support the video tag.</video>' +
                caption
            );
        }

        const yt = url.match(/(?:youtube\.com\/watch\?.*v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
        if (yt) {
            return (
                '<iframe class="wiki-video-player" src="https://www.youtube-nocookie.com/embed/' +
                yt[1] +
                '" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>' +
                caption
            );
        }

        const vimeo = url.match(/vimeo\.com\/(\d+)/);
        if (vimeo) {
            return (
                '<iframe class="wiki-video-player" src="https://player.vimeo.com/video/' +
                vimeo[1] +
                '" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>' +
                caption
            );
        }

        return null;
    }

    /**
     * ToastUI occasionally throws DOMException(NotFoundError) inside its vDOM
     * removeChild path during component teardown — harmless but loud. Suppress
     * just those, leave everything else alone.
     */
    private installVDOMErrorSuppression(): void {
        this.originalWindowOnError = window.onerror;
        window.onerror = (...args: any[]) => {
            if (this.isToastUIVDOMError(args[0], args[4])) {
                return true;
            }
            if (this.originalWindowOnError) {
                return (this.originalWindowOnError as Function).apply(window, args);
            }
            return false;
        };
        window.addEventListener('error', this.handleEditorVDOMError, true);
    }

    private isToastUIVDOMError(errOrMsg: any, errObj?: any): boolean {
        if (!(this.editor || this.destroyed)) return false;
        if (errOrMsg && typeof errOrMsg === 'object' && 'error' in errOrMsg) {
            const evt = errOrMsg as ErrorEvent;
            return (
                evt.error instanceof DOMException &&
                evt.error.name === 'NotFoundError' &&
                typeof evt.message === 'string' &&
                evt.message.indexOf('removeChild') !== -1
            );
        }
        if (
            errObj instanceof DOMException &&
            errObj.name === 'NotFoundError' &&
            typeof errOrMsg === 'string' &&
            errOrMsg.indexOf('removeChild') !== -1
        ) {
            return true;
        }
        if (typeof errOrMsg === 'string' && errOrMsg.indexOf('removeChild') !== -1) {
            return true;
        }
        return false;
    }

    private handleEditorVDOMError = (event: ErrorEvent) => {
        if (this.isToastUIVDOMError(event)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return true;
        }
    };

    private destroyEditor(): void {
        if (this.editor) {
            try {
                this.editor.destroy();
            } catch (e) {
                console.warn('gf-markdown-editor: destroy failed', e);
            }
            this.editor = null;
        }
    }

    public getMarkdown(): string {
        return this.editor?.getMarkdown() || '';
    }

    /**
     * Mark the host as a compact compose box, if the mode is on.
     */
    /**
     * Hide the compose toolbar even though the editor still has focus.
     *
     * For hosts where an action ends composing without ending focus -- sending
     * a chat message, say. Blurring instead would be wrong there: Enter is the
     * send key, so the user would have to click back into the box before every
     * single message.
     *
     * `:focus-within` alone cannot express "still focused, but done", which is
     * why this is a class rather than more CSS. It lifts again as soon as the
     * user does anything in the editor -- see releaseToolbar().
     */
    public suppressToolbar(): void {
        const host = this.elementRef?.nativeElement as HTMLElement;
        if (!host) return;
        host.classList.add(GfMarkdownEditorComponent.TOOLBAR_SUPPRESSED_CLASS);
    }

    /**
     * The user is composing again, so stop suppressing the toolbar.
     *
     * Called from the capture-phase keydown and mousedown listeners, which
     * between them cover both ways back in: typing the next message, and
     * clicking into the box.
     */
    private releaseToolbar(): void {
        const host = this.elementRef?.nativeElement as HTMLElement;
        if (!host) return;
        host.classList.remove(GfMarkdownEditorComponent.TOOLBAR_SUPPRESSED_CLASS);
    }

    private applyCompactClass(): void {
        const host = this.elementRef?.nativeElement as HTMLElement;
        if (!host) return;
        host.classList.toggle(GfMarkdownEditorComponent.COMPACT_CLASS, !!this.compact);
    }

    public setMarkdown(markdown: string): void {
        if (!this.editor) return;
        this.isUpdating = true;
        try {
            this.editor.setMarkdown(markdown);
        } catch (e) {
            console.warn('gf-markdown-editor: setMarkdown failed', e);
        }
        this.isUpdating = false;
    }

    public focus(): void {
        this.editor?.focus();
    }

    public insertText(text: string): void {
        this.editor?.insertText(text);
    }

    private onMentionEditorContentChanged(): void {
        if (!this.isMentionsEnabled()) {
            this.resetMentionSearchState();
            this.exitMentionContext();
            return;
        }

        const query = this.getActiveMentionQuery();
        if (query === null) {
            this.resetMentionSearchState();
            this.exitMentionContext();
            return;
        }

        if (query !== this.lastMentionSearchTerm) {
            this.lastMentionSearchTerm = query;
            this.searchTerm.emit(query);
        }

        this.mentionQuery = query;
        this.refreshMentionDropdown();
    }

    private isMentionsEnabled(): boolean {
        return Array.isArray(this.mentions) && this.mentions.length > 0;
    }

    private resetMentionSearchState(): void {
        if (this.lastMentionSearchTerm !== '') {
            this.lastMentionSearchTerm = '';
            this.searchTerm.emit('');
        }
    }

    // ---------------------------------------------------------------------
    // @mention autocomplete
    //
    // ToastUI presents a contenteditable in both modes, so the caret-coordinate
    // logic the `[mentions]` directive uses for a <textarea> does not apply.
    // The list component itself is reused as-is; only discovery, positioning,
    // key handling and insertion are done here.
    // ---------------------------------------------------------------------

    /**
     * The mention configuration in play. Matches the directive's behaviour of
     * working from the first configured list.
     */
    private get activeMentionConfig(): MentionListConfig | null {
        return this.isMentionsEnabled() ? this.mentions[0] : null;
    }

    get mentionLabelKey(): string {
        return this.activeMentionConfig?.labelKey || 'label';
    }

    /**
     * Narrow the configured items down to what the popup should show.
     *
     * `disableSearch` decides who owns matching. With it set, `items` is
     * already the answer to the current query: the host filtered and ranked
     * it, so we neither filter nor re-sort -- an alphabetical pass here would
     * flatten a prefix-first ranking the host went to the trouble of building.
     *
     * Without it, `items` is the full candidate set and we do the matching
     * ourselves: prefix match on the label key, sorted alphabetically.
     */
    private filterMentionItems(config: MentionListConfig, query: string): MentionItem[] {
        const labelKey = config.labelKey || 'label';
        let items = (config.items || []).filter((item) => item && item[labelKey]);

        if (!config.disableSearch) {
            if (query) {
                const needle = query.toLowerCase();
                items = items.filter((item) => String(item[labelKey]).toLowerCase().startsWith(needle));
            }
            items = items.slice().sort((a, b) => String(a[labelKey]).localeCompare(String(b[labelKey])));
        }

        return config.maxItems > 0 ? items.slice(0, config.maxItems) : items;
    }

    /**
     * Show or update the popup for the current query. Called both when the user
     * types and when the host swaps in fresh `[mentions]` after an async lookup.
     */
    private refreshMentionDropdown(): void {
        const config = this.activeMentionConfig;
        if (!config || !this.mentionListCmp || this.mentionQuery === null) {
            this.closeMentionDropdown();
            return;
        }

        const matches = this.filterMentionItems(config, this.mentionQuery);
        if (!matches.length) {
            this.closeMentionDropdown();
            return;
        }

        this.mentionListCmp.items = matches;
        this.mentionListCmp.activeIndex = 0;
        this.mentionListCmp.hidden = false;
        this.mentionListCmp.resetScroll();
        this.mentionOpen = true;
        this.positionMentionDropdown();
    }

    /**
     * Hide the popup but stay in the mention context.
     *
     * Deliberately does NOT clear `mentionQuery`: with an async host the first
     * keystroke usually matches nothing locally, and the results only arrive a
     * round-trip later. Forgetting the query here would mean the popup could
     * never open for exactly the lookups it exists to serve.
     */
    private closeMentionDropdown(): void {
        if (!this.mentionOpen) return;
        this.mentionOpen = false;
        if (this.mentionListCmp) {
            this.mentionListCmp.hidden = true;
            this.mentionListCmp.items = [];
        }
    }

    /**
     * Leave the mention context entirely -- the caret moved away, the trigger
     * was deleted, or a mention was just inserted.
     */
    private exitMentionContext(): void {
        this.mentionQuery = null;
        this.closeMentionDropdown();
    }

    /**
     * Park the popup just under the caret.
     *
     * Both modes are the same arithmetic against a different reference box: the
     * host's own rect when positioning absolutely inside it (the host is already
     * position:relative to anchor the mode-toggle button), and the viewport
     * itself under `mentionsFixed`, to escape a clipping ancestor.
     */
    private positionMentionDropdown(): void {
        const listEl = this.mentionListEl?.nativeElement as HTMLElement;
        const host = this.elementRef?.nativeElement as HTMLElement;
        const caret = this.caretRect();
        if (!listEl || !caret) return;
        if (!this.mentionsFixed && !host) return;

        const base = this.mentionsFixed
            ? { left: 0, top: 0, bottom: window.innerHeight }
            : host.getBoundingClientRect();

        listEl.style.position = this.mentionsFixed ? 'fixed' : 'absolute';
        listEl.style.left = `${ caret.left - base.left }px`;

        if (this.mentionsAbove) {
            // Anchored by its bottom edge rather than its top, so the list's own
            // height never has to be measured -- `hidden` is cleared as a
            // property and does not reach the DOM until the next change
            // detection pass, so the element still measures zero here.
            listEl.style.top = 'auto';
            listEl.style.bottom = `${ base.bottom - caret.top + 4 }px`;
        } else {
            // Reset the other edge: the popup element is reused across opens.
            listEl.style.bottom = 'auto';
            listEl.style.top = `${ caret.bottom - base.top + 4 }px`;
        }
    }

    /**
     * Viewport rect of the collapsed caret.
     *
     * A collapsed range sitting in an empty text node reports a zero rect, so
     * fall back to the range's own client rects and then to the enclosing
     * element before giving up.
     */
    private caretRect(): DOMRect | null {
        if (typeof window === 'undefined') return null;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return null;

        const range = selection.getRangeAt(0).cloneRange();
        range.collapse(true);

        let rect: DOMRect | null = range.getBoundingClientRect();
        if (!rect || (!rect.top && !rect.left && !rect.height)) {
            const rects = range.getClientRects();
            if (rects.length) {
                rect = rects[0] as DOMRect;
            } else {
                const node = range.startContainer;
                const el = (node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement) as HTMLElement | null;
                rect = el ? el.getBoundingClientRect() : null;
            }
        }

        return rect;
    }

    /**
     * Capture-phase key handling for the whole editor.
     *
     * Capture, not bubble: ToastUI's ProseMirror view handles keydown on the
     * inner contenteditable, so by the time an event reaches the host it has
     * already inserted the newline or moved the caret. Anything that needs to
     * pre-empt the editor has to be caught on the way down.
     *
     * The mention popup wins when it is open -- while the user is picking a
     * name, Enter belongs to the popup, not to submit.
     */
    private onEditorKeyDown = (event: KeyboardEvent): void => {
        // Typing means composing has resumed. Runs before the submit check, so
        // the keystroke that *sends* re-suppresses the toolbar rather than
        // leaving it revealed by its own release.
        this.releaseToolbar();

        if (this.handleMentionKeys(event)) {
            return;
        }

        if (this.isSubmitKey(event)) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            // Settle the debounced contentChange before telling the host to
            // submit. The host's bound model trails typing by up to the debounce
            // interval, so without this the send reads a model that is a few
            // keystrokes stale -- type quickly, press Enter, and the tail of the
            // message is silently dropped. Flushing is a no-op when nothing is
            // pending, so a slow typist's path is unchanged.
            this.debouncedContentChange.flush();

            this.submitShortcut.emit(event);
        }
    }

    /**
     * Navigation keys belonging to the mention popup, for either mode.
     *
     * @returns true when the key was consumed and the caller must stop.
     */
    private handleMentionKeys(event: KeyboardEvent): boolean {
        if (!this.mentionOpen || !this.mentionListCmp) {
            return false;
        }

        const stop = () => {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        };

        switch (event.key) {
            case 'ArrowDown':
                this.mentionListCmp.activateNextItem();
                stop();
                return true;
            case 'ArrowUp':
                this.mentionListCmp.activatePreviousItem();
                stop();
                return true;
            case 'Enter':
            case 'Tab':
                this.insertActiveMention();
                stop();
                return true;
            case 'Escape':
                this.closeMentionDropdown();
                stop();
                return true;
            default:
                // Any other key while the popup is open is ordinary typing,
                // but it still belongs to the popup's turn -- the caller must
                // not treat Enter-like keys as submit until it closes.
                return false;
        }
    }

    /**
     * Is this the "send it" keystroke?
     *
     * Shift+Enter is the line break, so it must fall through. Enter mid-IME
     * composition is the user committing a candidate word, not sending -- CJK
     * input would be unusable otherwise. Ctrl/Cmd+Enter is accepted too, since
     * it is the reflex in most other message composers.
     */
    private isSubmitKey(event: KeyboardEvent): boolean {
        if (!this.submitOnEnter || event.key !== 'Enter') return false;
        if (event.shiftKey) return false;
        if (event.isComposing || (event as any).keyCode === 229) return false;
        return true;
    }

    /**
     * Replace the typed `@query` with the configured mention text.
     *
     * Public because the list emits (itemClick) on mouse selection.
     */
    public insertActiveMention(): void {
        const config = this.activeMentionConfig;
        const item = this.mentionListCmp?.activeItem;
        const query = this.mentionQuery;

        if (!config || !item || query === null) {
            this.exitMentionContext();
            return;
        }

        const labelKey = config.labelKey || 'label';
        const text = config.mentionSelect ? config.mentionSelect(item) : `@${ item[labelKey] } `;

        // '@' plus everything typed after it.
        this.replaceBeforeCaret(query.length + 1, text);
        this.exitMentionContext();
        this.debouncedContentChange();
    }

    /**
     * Replace the `length` characters preceding the caret with `text`.
     *
     * getSelection() reports [[line, ch], [line, ch]] in Markdown mode (1-based)
     * and a flat [from, to] offset pair in WYSIWYG, so both shapes are handled.
     */
    private replaceBeforeCaret(length: number, text: string): void {
        if (!this.editor) return;

        try {
            const selection = this.editor.getSelection();
            const end = selection[1];

            if (Array.isArray(end)) {
                const [line, ch] = end as [number, number];
                this.editor.replaceSelection(text, [line, Math.max(1, ch - length)], [line, ch]);
            } else {
                const to = end as number;
                this.editor.replaceSelection(text, Math.max(0, to - length), to);
            }
        } catch (e) {
            // Never let a failed insertion wedge the editor; the user can retype.
            console.warn('gf-markdown-editor: mention insert failed', e);
        }
    }

    /**
     * Watch for ToastUI popups opening and pull them back on screen.
     *
     * The popup lives inside the toolbar and is shown by flipping its inline
     * `display`/`top`/`left`, so an attribute observer on `style` sees every
     * open. Repositioning writes `style` too, which would re-trigger the
     * observer — a data attribute marks a popup as already handled for this
     * open, and is cleared when it hides.
     */
    private bindPopupViewportGuard(): void {
        if (!this.keepPopupsInView || this.popupObserver) return;

        const container = this.editorContainer?.nativeElement as HTMLElement;
        if (!container || typeof MutationObserver === 'undefined') return;

        this.popupObserver = new MutationObserver((records) => {
            records.forEach((record) => {
                const el = record.target as HTMLElement;
                if (el && el.classList && el.classList.contains('toastui-editor-popup')) {
                    this.fitPopupInViewport(el);
                }
            });
        });

        this.popupObserver.observe(container, {
            attributes: true,
            attributeFilter: ['style'],
            subtree: true,
        });
    }

    private unbindPopupViewportGuard(): void {
        if (!this.popupObserver) return;
        this.popupObserver.disconnect();
        this.popupObserver = null;
    }

    private fitPopupInViewport(popup: HTMLElement): void {
        const hidden = popup.style.display === 'none' || popup.offsetParent === null;
        if (hidden) {
            // Fit again the next time this popup is opened, from wherever the
            // toolbar happens to be then. (removeAttribute rather than `delete`
            // on dataset -- same effect, no TS quibble about optionality. The
            // observer only watches `style`, so this does not re-enter.)
            popup.removeAttribute('data-gf-viewport-fitted');
            return;
        }

        if (popup.dataset.gfViewportFitted === '1') return;

        const margin = GfMarkdownEditorComponent.POPUP_VIEWPORT_MARGIN;
        const rect = popup.getBoundingClientRect();
        const styleTop = parseFloat(popup.style.top) || 0;
        const styleLeft = parseFloat(popup.style.left) || 0;

        // Translate between viewport coordinates and whatever coordinate space
        // the inline top/left are expressed in, without needing to know which
        // ancestor is the offset parent.
        const viewportTopInStyleSpace = styleTop - rect.top;
        const viewportLeftInStyleSpace = styleLeft - rect.left;

        let top = styleTop;
        if (rect.bottom > window.innerHeight - margin) {
            // Flip it above the button. ToastUI opens the popup directly below
            // the toolbar button, so subtracting its own height puts its bottom
            // roughly where its top was -- it now grows upward from the button,
            // which is what a compose box pinned to the bottom of the screen
            // needs.
            top = styleTop - rect.height - margin;

            const minTop = viewportTopInStyleSpace + margin;
            if (top < minTop) {
                top = minTop;
            }
        }

        let left = styleLeft;
        const overflowRight = rect.right - (window.innerWidth - margin);
        if (overflowRight > 0) {
            left = styleLeft - overflowRight;
        }
        const minLeft = viewportLeftInStyleSpace + margin;
        if (left < minLeft) {
            left = minLeft;
        }

        // Marked before writing: the style writes below re-enter this observer.
        popup.dataset.gfViewportFitted = '1';

        if (top !== styleTop) {
            popup.style.top = `${ top }px`;
        }
        if (left !== styleLeft) {
            popup.style.left = `${ left }px`;
        }
    }

    private bindEditorKeyListener(): void {
        const container = this.editorContainer?.nativeElement as HTMLElement;
        if (!container || this.editorKeyListener) return;
        const listener = this.onEditorKeyDown;
        this.editorKeyListener = listener;
        container.addEventListener('keydown', listener, true);
    }

    private unbindEditorKeyListener(): void {
        const container = this.editorContainer?.nativeElement as HTMLElement;
        const listener = this.editorKeyListener;
        if (!container || !listener) return;
        container.removeEventListener('keydown', listener, true);
        this.editorKeyListener = null;
    }

    private getActiveMentionQuery(): string | null {
        const text = this.getTextBeforeCaret();
        if (text === null) {
            return null;
        }
        const match = text.match(/(?:^|[\s([{])@([\w.-]*)$/);
        return match ? (match[1] || '') : null;
    }

    private getTextBeforeCaret(): string | null {
        const container = this.editorContainer && this.editorContainer.nativeElement as HTMLElement;
        if (!container || typeof window === 'undefined' || typeof document === 'undefined') {
            return null;
        }

        const active = document.activeElement as HTMLInputElement | null;
        if (active && container.contains(active) && typeof active.value === 'string' && typeof active.selectionStart === 'number') {
            return active.value.substring(0, active.selectionStart);
        }

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || !selection.anchorNode || !container.contains(selection.anchorNode)) {
            return null;
        }

        const caretRange = selection.getRangeAt(0).cloneRange();
        caretRange.collapse(true);
        const pre = selection.getRangeAt(0).cloneRange();
        pre.selectNodeContents(this.mentionScopeFor(selection.anchorNode, container));
        pre.setEnd(caretRange.endContainer, caretRange.endOffset);
        return pre.toString();
    }

    /**
     * The element to measure "text before the caret" against.
     */
    private mentionScopeFor(anchorNode: Node, container: HTMLElement): HTMLElement {
        const start = (anchorNode.nodeType === Node.TEXT_NODE ? anchorNode.parentElement : anchorNode) as Element | null;
        if (!start || typeof start.closest !== 'function') {
            return container;
        }

        const block = start.closest(
            'p, li, h1, h2, h3, h4, h5, h6, blockquote, pre, td, th, dd, dt, figcaption, .cm-line'
        ) as HTMLElement | null;
        if (block && container.contains(block)) {
            return block;
        }
        const editable = start.closest('[contenteditable="true"]') as HTMLElement | null;
        if (!editable || !container.contains(editable)) {
            return container;
        }

        let line = start as HTMLElement;
        while (line !== editable && line.parentElement !== editable) {
            line = line.parentElement as HTMLElement;
        }
        return line;
    }

    private isEditorFocused(): boolean {
        const container = this.editorContainer?.nativeElement as HTMLElement;
        if (!container || typeof document === 'undefined') {
            return false;
        }
        const active = document.activeElement as Node | null;
        return !!active && container.contains(active);
    }
}
